import { gql, useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { LockResetOutlined } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { clearPasswordChangeRequirement } from "state/authSlice";

const CHANGE_PASSWORD = gql`
  mutation ChangePassword($email: String!, $password: String!, $oldPassword: String!) {
    changePassword(email: $email, password: $password, oldPassword: $oldPassword) {
      changePassword
    }
  }
`;

const ForcePasswordChangePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const email = localStorage.getItem("email");

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [changePassword, { loading }] = useMutation(CHANGE_PASSWORD);

  const validation = useMemo(() => {
    const errors = {};
    if (!oldPassword.trim()) errors.oldPassword = "Current password is required.";
    if (!newPassword.trim()) errors.newPassword = "New password is required.";
    if (newPassword.trim().length > 0 && newPassword.trim().length < 8) {
      errors.newPassword = "Use at least 8 characters for better account security.";
    }
    if (!confirmPassword.trim()) errors.confirmPassword = "Please confirm your new password.";
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }
    if (oldPassword && newPassword && oldPassword === newPassword) {
      errors.newPassword = "New password must be different from your current password.";
    }
    return errors;
  }, [oldPassword, newPassword, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    if (Object.keys(validation).length > 0) return;
    if (!email) {
      setErrorMsg("Your session is missing account details. Please sign in again.");
      return;
    }

    try {
      const { data } = await changePassword({
        variables: {
          email,
          password: newPassword,
          oldPassword,
        },
      });

      if (data?.changePassword?.changePassword === false) {
        localStorage.setItem("requiresPasswordChange", "false");
        dispatch(clearPasswordChangeRequirement());
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setSuccessMsg("Password changed successfully. Redirecting to your dashboard...");
        setTimeout(() => navigate("/dashboard", { replace: true }), 700);
        return;
      }

      setErrorMsg("Password update is not complete yet. Please try again.");
    } catch (error) {
      const rawMessage = error?.message || "";
      if (rawMessage.toLowerCase().includes("old password")) {
        setErrorMsg("The current password you entered is incorrect.");
      } else if (rawMessage.toLowerCase().includes("unauthorized")) {
        setErrorMsg("Your session has expired. Please sign in again.");
      } else {
        setErrorMsg("We could not update your password right now. Please retry.");
      }
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2 }}>
      <Card sx={{ maxWidth: 560, width: "100%", borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack spacing={2}>
            <Box display="flex" alignItems="center" gap={1.25}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  color: theme.palette.primary.contrastText,
                  backgroundColor: theme.palette.primary.main,
                }}
              >
                <LockResetOutlined />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={800}>
                  Secure your account
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Welcome to Centra. Please set a new password to continue.
                </Typography>
              </Box>
            </Box>

            {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}
            {successMsg ? <Alert severity="success">{successMsg}</Alert> : null}

            <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2 }}>
              <TextField
                type="password"
                label="Current password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                error={!!validation.oldPassword}
                helperText={validation.oldPassword}
                autoComplete="current-password"
                fullWidth
              />
              <TextField
                type="password"
                label="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                error={!!validation.newPassword}
                helperText={validation.newPassword || "Use at least 8 characters."}
                autoComplete="new-password"
                fullWidth
              />
              <TextField
                type="password"
                label="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={!!validation.confirmPassword}
                helperText={validation.confirmPassword}
                autoComplete="new-password"
                fullWidth
              />

              <Typography variant="caption" color="text.secondary">
                For your security, this step is required before you can access balances, loans, and transactions.
              </Typography>

              <Button
                type="submit"
                variant="contained"
                disabled={loading || Object.keys(validation).length > 0}
                sx={{ mt: 1, py: 1.2, borderRadius: 2, fontWeight: 700 }}
              >
                {loading ? <CircularProgress size={18} color="inherit" /> : "Update password"}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ForcePasswordChangePage;
